import { useCallback, useEffect, useMemo, useRef } from 'react';
import posthog from 'posthog-js';
import { EVENTS } from '../constants/events';
import { useAnalytics } from './useAnalytics';

/**
 * Analytics for the web2app funnel (`/funnel-auth`).
 *
 * The whole funnel is one route with steps held in React state, so PostHog's
 * automatic pageview capture can only ever see a single page for the entire
 * flow. Every step therefore reports itself twice, on purpose:
 *
 *  1. `funnel_step_viewed` (+ `step`/`step_number`) — the event to build the
 *     PostHog funnel insight on. `step_number` makes the drop-off chart order
 *     itself without anyone hand-sorting step names in the UI.
 *  2. a synthetic `$pageview` on a virtual path (`/funnel-auth/<step>`) — so
 *     the step also shows up as its own page in Web Analytics / Paths, which
 *     is where funnel steps are usually looked for first.
 *
 * The virtual pageview deliberately carries no query string: the funnel URL
 * holds `redirect_uri`/`state` and, on the way back from Google, the OAuth
 * `code` — none of which belong in analytics.
 */

const FUNNEL_NAME = 'web2app';

// Ordered funnel views, including the transient ones ('generating',
// 'finishing') that no one clicks but everyone passes through. Index + 1 is
// the reported `step_number`.
export const FUNNEL_VIEW_ORDER = [
  'welcome',
  'name',
  'age',
  'concern',
  'create',
  'generating',
  'meet',
  'signin',
  'finishing',
];

// Fires `funnel_started` once per funnel session rather than once per page
// load — the Google round trip reloads the page mid-flow, and a reload isn't
// a new start.
export const FUNNEL_STARTED_KEY = 'rc_funnel_started';

/**
 * @param {object} context Funnel answers so far. Merged into every event, so
 *   drop-off can be sliced by age bucket / concern without a join. Read
 *   through a ref, so handlers always send the latest values.
 */
export const useFunnelAnalytics = (context = {}) => {
  const { trackEvent } = useAnalytics();

  const contextRef = useRef(context);
  useEffect(() => {
    contextRef.current = context;
  });

  const lastViewRef = useRef(null);
  const invalidLinkFiredRef = useRef(false);

  const track = useCallback(
    (event, properties = {}) => {
      trackEvent(event, {
        funnel: FUNNEL_NAME,
        ...contextRef.current,
        ...properties,
      });
    },
    [trackEvent]
  );

  // For events fired immediately before leaving the page (the redirect back to
  // the RevenueCat funnel, the hop out to Google). PostHog normally batches
  // captures, and a navigation can outrun the next flush — `send_instantly`
  // skips the queue so the conversion isn't the one event that goes missing.
  const trackImmediate = useCallback(
    (event, properties = {}) => {
      try {
        posthog.capture(
          event,
          {
            funnel: FUNNEL_NAME,
            ...contextRef.current,
            ...properties,
            // Matches what useAnalytics.trackEvent attaches to every event
            timestamp: new Date().toISOString(),
            source: 'pixley_web_app',
          },
          { send_instantly: true }
        );
      } catch (_) {
        // Analytics must never break the funnel.
      }
    },
    []
  );

  const stepProps = useCallback(
    (view, properties = {}) => ({
      step: view,
      step_number: FUNNEL_VIEW_ORDER.indexOf(view) + 1,
      total_steps: FUNNEL_VIEW_ORDER.length,
      ...properties,
    }),
    []
  );

  const trackView = useCallback(
    (view) => {
      // Guards double-firing from React StrictMode's dev double-effects and
      // from re-renders that don't change the view.
      if (!view || lastViewRef.current === view) return;
      lastViewRef.current = view;

      if (!sessionStorage.getItem(FUNNEL_STARTED_KEY)) {
        sessionStorage.setItem(FUNNEL_STARTED_KEY, '1');
        track(EVENTS.FUNNEL_STARTED, {
          entry_step: view,
          // A visitor entering anywhere but the welcome screen resumed a
          // funnel they'd already started (reload, or back from Google).
          is_resumed: view !== 'welcome',
        });
      }

      track(EVENTS.FUNNEL_STEP_VIEWED, stepProps(view));

      try {
        const path = `/funnel-auth/${view}`;
        posthog.capture('$pageview', {
          $current_url: `${window.location.origin}${path}`,
          $pathname: path,
          funnel: FUNNEL_NAME,
          ...stepProps(view),
        });
      } catch (_) {
        // Analytics must never break the funnel.
      }
    },
    [track, stepProps]
  );

  // `immediate` for the sign-in step, whose completion is followed straight
  // away by the redirect out to Google.
  const trackStepCompleted = useCallback(
    (view, properties, { immediate = false } = {}) =>
      (immediate ? trackImmediate : track)(
        EVENTS.FUNNEL_STEP_COMPLETED,
        stepProps(view, properties)
      ),
    [track, trackImmediate, stepProps]
  );

  const trackInvalidLink = useCallback(() => {
    if (invalidLinkFiredRef.current) return;
    invalidLinkFiredRef.current = true;
    track(EVENTS.FUNNEL_INVALID_LINK, {
      // Which half is missing tells a broken RevenueCat funnel config apart
      // from someone finding the URL on their own.
      has_redirect_uri: !!new URLSearchParams(window.location.search).get('redirect_uri'),
    });
  }, [track]);

  const setPersonProperties = useCallback((properties) => {
    try {
      posthog.setPersonProperties(properties);
    } catch (_) {
      // ignore
    }
  }, []);

  return useMemo(
    () => ({
      track,
      trackImmediate,
      trackView,
      trackStepCompleted,
      trackInvalidLink,
      setPersonProperties,
    }),
    [track, trackImmediate, trackView, trackStepCompleted, trackInvalidLink, setPersonProperties]
  );
};

export default useFunnelAnalytics;
